import { useEffect, useState } from "react";
import Footer from "../components/footer";
import {
  Typography,
} from "@material-tailwind/react";
import { API_URL, MEDIA_URL } from "../App.js";
import { GiPlantsAndAnimals } from "react-icons/gi";
import { BsFillCpuFill } from "react-icons/bs";
import { AiFillMedicineBox } from "react-icons/ai";
import PublicationFlipCard from "../components/Publications_card/publications_flip_card.jsx";

const getIcon = (iconName) => {
  switch (iconName) {
    case "Biología computacional":
      return <GiPlantsAndAnimals />;
    case "Computación de alto desempeño":
      return <BsFillCpuFill />;
    case "Patología Computacional":
      return <AiFillMedicineBox />;
    default:
      return null;
  }
};

const Investigation = () => {
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Publicaciones por página

  useEffect(() => {
    fetch(API_URL + "publicaciones")
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData.data) {
          setData(responseData.data);
          // Seleccionar "Todas" por defecto
          setSelectedTipo(null);
        } else {
          console.error("No se recibieron los datos esperados de la API");
        }
      })
      .catch((error) => console.error(error));
  }, []);

  // Resetear a la primera página cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTipo]);

  if (!data) {
    return (
      <>
        <br />
        <Typography variant="h3" className="font-bold font-sans" color="gray">
          Loading...
        </Typography>
      </>
    );
  }

  // Filtrar publicaciones según el tipo seleccionado
  const filteredPublications = selectedTipo
    ? data.publicacion.filter((pub) => pub.tipo_publicacion_id === selectedTipo)
    : data.publicacion;

  // Calcular paginación
  const totalPages = Math.ceil(filteredPublications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPublications = filteredPublications.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col flex-grow bg-gray-50">
      <br />
      
      {/* Barra de filtros por tipo de publicación */}
      <div className="flex flex-wrap justify-center gap-2 mb-1 mx-4">
        <button
          onClick={() => setSelectedTipo(null)}
          className={`px-4 py-2 rounded-full font-sans font-medium transition-all duration-300 flex items-center gap-2 ${
            selectedTipo === null
              ? "bg-colorPrincipal text-white shadow-lg"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          <span>Todas</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            selectedTipo === null
              ? "bg-white/20"
              : "bg-gray-300"
          }`}>
            {data.publicacion.length}
          </span>
        </button>
        {data.tipo_publicacion.map((tipo) => {
          const count = data.publicacion.filter(pub => pub.tipo_publicacion_id === tipo.id).length;
          return (
            <button
              key={tipo.id}
              onClick={() => setSelectedTipo(tipo.id)}
              className={`px-4 py-2 rounded-full font-sans font-medium transition-all duration-300 flex items-center gap-2 ${
                selectedTipo === tipo.id
                  ? "bg-colorPrincipal text-white shadow-lg"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {getIcon(tipo.nombre)}
              <span>{tipo.nombre}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                selectedTipo === tipo.id
                  ? "bg-white/20"
                  : "bg-gray-300"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Grid de publicaciones con Flip Cards */}
      <div className="m-10 font-sans">
        <div className="flip-cards-container-pub">
          {currentPublications.length > 0 ? (
            currentPublications.map((pub) => (
              <PublicationFlipCard 
                key={pub.id} 
                publicacion={pub} 
                mediaUrl={MEDIA_URL}
                tiposPublicacion={data.tipo_publicacion}
              />
            ))
          ) : (
            <div className="no-results-pub">
              No hay publicaciones disponibles para este filtro.
            </div>
          )}
        </div>

        {/* Paginador */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8 mb-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 font-sans font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Anterior
            </button>
            
            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                // Mostrar solo algunas páginas alrededor de la actual
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`w-10 h-10 rounded-lg font-sans font-medium transition-all ${
                        currentPage === page
                          ? 'bg-colorPrincipal text-white shadow-lg'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return <span key={page} className="px-2">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 font-sans font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Siguiente
            </button>
          </div>
        )}

        {/* Indicador de resultados */}
        <div className="text-center text-gray-600 text-sm font-sans mt-4">
          Mostrando {startIndex + 1} - {Math.min(endIndex, filteredPublications.length)} de {filteredPublications.length} publicaciones
        </div>
      </div>
      <br />
      <br />
      <br />
      <Footer />
    </div>
  );
};

export default Investigation;
